import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { NotebookPen, Search, ArrowUpRight } from "lucide-react";

const fmtDate = (iso) => (iso || "").slice(0, 16).replace("T", " · ");

export default function MyNotes() {
  const [notes, setNotes] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/notes").then(r => setNotes(r.data)).catch(() => setNotes([]));
  }, []);

  const grouped = useMemo(() => {
    if (!notes) return [];
    const term = q.trim().toLowerCase();
    const visible = notes.filter(n => (n.text || "").trim() &&
      (!term || n.text.toLowerCase().includes(term) ||
       (n.lesson_title || "").toLowerCase().includes(term) ||
       (n.module_title || "").toLowerCase().includes(term)));
    const byModule = new Map();
    for (const n of visible) {
      const key = n.module_id;
      if (!byModule.has(key)) byModule.set(key, { module_id: key, module_title: n.module_title || "Untitled module", notes: [] });
      byModule.get(key).notes.push(n);
    }
    return [...byModule.values()];
  }, [notes, q]);

  if (!notes) return <div className="text-sm text-neutral-500" data-testid="my-notes-loading">Loading your notes…</div>;

  const total = notes.filter(n => (n.text || "").trim()).length;

  return (
    <div className="space-y-8" data-testid="my-notes-page">
      <div>
        <div className="label-eyebrow mb-3">Revision</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">My Notes</h1>
        <p className="text-neutral-500 mt-3">Everything you've jotted down across the curriculum — in one place before your next live call.</p>
      </div>

      {total === 0 ? (
        <div className="border hairline bg-white p-8 text-center" data-testid="my-notes-empty">
          <NotebookPen size={22} className="mx-auto text-[var(--brand-primary)]" />
          <p className="text-sm text-neutral-500 mt-3 max-w-md mx-auto">
            No notes yet. Open any lesson and use the "My notes" panel — everything you write is saved here automatically.
          </p>
          <Link to="/dashboard" className="btn-primary text-sm inline-block mt-5" data-testid="my-notes-start">Go to my journey</Link>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 border hairline bg-white px-4 py-2 max-w-md">
            <Search size={14} className="text-neutral-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder={`Search ${total} note${total !== 1 ? "s" : ""}…`}
                   className="flex-1 bg-transparent text-sm focus:outline-none" data-testid="my-notes-search" />
          </div>

          {grouped.length === 0 && (
            <div className="text-sm text-neutral-500" data-testid="my-notes-no-match">No notes match "{q}".</div>
          )}

          <div className="space-y-8">
            {grouped.map(g => (
              <section key={g.module_id} data-testid={`notes-module-${g.module_id}`}>
                <h2 className="font-display text-lg mb-3">{g.module_title}</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {g.notes.map(n => (
                    <div key={`${n.module_id}-${n.lesson_index}`} className="border hairline bg-white p-5 flex flex-col"
                         data-testid={`note-card-${n.module_id}-${n.lesson_index}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-sm font-medium">{n.lesson_title || `Lesson ${n.lesson_index + 1}`}</div>
                        <Link to={`/module/${n.module_id}?lesson=${n.lesson_index}`}
                              className="text-xs text-[var(--brand-primary)] flex items-center gap-1 shrink-0 hover:underline"
                              data-testid={`note-open-${n.module_id}-${n.lesson_index}`}>
                          Open lesson <ArrowUpRight size={11} />
                        </Link>
                      </div>
                      <p className="text-sm text-neutral-600 mt-3 whitespace-pre-wrap flex-1">{n.text}</p>
                      <div className="font-mono text-xs text-neutral-400 mt-4">{fmtDate(n.updated_at)}</div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
